import React, { useEffect, useMemo, useState } from 'react';
import ClassicScreenShell from './ClassicScreenShell';
import { getErrorMessage } from '../utils/errorHandler';

interface ClassicMonthlySummaryProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

interface MonthlyData {
  year: number;
  month: number;
  period_start: string;
  period_end: string;
  cash_flow: {
    total_income: number;
    total_expense: number;
    net_cash_flow: number;
  } | null;
  business_activity: {
    net_change: number;
  } | null;
}

interface CashFlowResponse {
  period_start: string;
  period_end: string;
  categories: Array<{
    category_id: number;
    label: string;
    total: number;
    rows: Array<{
      daikubun: string;
      amount: number;
    }>;
  }>;
  total_income: number;
  total_expense: number;
  net_cash_flow: number;
}

interface BusinessActivityResponse {
  period_start: string;
  period_end: string;
  categories: Array<{
    category_id: number;
    label: string;
    total: number;
  }>;
  net_change: number;
}

const currencyFormatter = new Intl.NumberFormat('ja-JP');

const formatCurrency = (amount: number): string => {
  return `¥${currencyFormatter.format(amount)}`;
};

const getCurrentFiscalYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  // 4月始まりの年度
  if (month >= 4) {
    return year;
  }
  return year - 1;
};

const getFiscalYearMonths = (fiscalYear: number) => {
  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const year = month >= 4 ? fiscalYear : fiscalYear + 1;
    return {
      year,
      month,
      label: `${year}年${month}月`,
      period_start: `${year}-${String(month).padStart(2, '0')}-01`,
      period_end: `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`,
    };
  });
};

const ClassicMonthlySummary: React.FC<any> = ({
  onMenuSelect,
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const currentFiscalYear = useMemo(() => getCurrentFiscalYear(), []);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(currentFiscalYear);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const fiscalYearMonths = useMemo(() => getFiscalYearMonths(selectedFiscalYear), [selectedFiscalYear]);

  const fetchMonthlyData = async () => {
    if (currentTenantId == null) {
      setError('テナントが選択されていません');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dataPromises = fiscalYearMonths.map(async (monthInfo) => {
        try {
          // 資金収支計算書データ取得
          const cashFlowResponse = await apiFetch(
            `/api/cash-flow-statement?period_start=${monthInfo.period_start}&period_end=${monthInfo.period_end}`,
          );
          if (!cashFlowResponse.ok) {
            const errorMessage = await getErrorMessage(
              cashFlowResponse,
              '資金収支計算書データの取得に失敗しました',
            );
            throw new Error(errorMessage);
          }
          const cashFlowData: CashFlowResponse | null = await cashFlowResponse.json();

          // 事業活動収支計算書データ取得
          const businessActivityResponse = await apiFetch(
            `/api/business-activity-statement?period_start=${monthInfo.period_start}&period_end=${monthInfo.period_end}`,
          );
          if (!businessActivityResponse.ok) {
            const errorMessage = await getErrorMessage(
              businessActivityResponse,
              '事業活動収支計算書データの取得に失敗しました',
            );
            throw new Error(errorMessage);
          }
          const businessActivityData: BusinessActivityResponse | null =
            await businessActivityResponse.json();

          return {
            year: monthInfo.year,
            month: monthInfo.month,
            period_start: monthInfo.period_start,
            period_end: monthInfo.period_end,
            cash_flow: cashFlowData
              ? {
                total_income: cashFlowData.total_income || 0,
                total_expense: cashFlowData.total_expense || 0,
                net_cash_flow: cashFlowData.net_cash_flow || 0,
              }
              : null,
            business_activity: businessActivityData
              ? {
                net_change: businessActivityData.net_change || 0,
              }
              : null,
          };
        } catch (err) {
          console.error(`月次データ取得エラー (${monthInfo.label}):`, err);
          return {
            year: monthInfo.year,
            month: monthInfo.month,
            period_start: monthInfo.period_start,
            period_end: monthInfo.period_end,
            cash_flow: null,
            business_activity: null,
          };
        }
      });

      const results = await Promise.all(dataPromises);
      setMonthlyData(results);
    } catch (err: any) {
      console.error('月次データ取得エラー:', err);
      setError(err?.message || '月次データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentTenantId != null) {
      fetchMonthlyData();
    }
  }, [selectedFiscalYear, currentTenantId]);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(event.target.value, 10);
    setSelectedFiscalYear(newYear);
  };

  const handleRefresh = () => {
    fetchMonthlyData();
  };

  const handleDownloadPDF = async () => {
    if (currentTenantId == null) {
      setError('テナントが選択されていません');
      return;
    }

    setPdfGenerating(true);
    setError(null);

    try {
      const periodStart = `${selectedFiscalYear}-04-01`;
      const periodEnd = `${selectedFiscalYear + 1}-03-31`;

      // 資金収支計算書のPDFをダウンロード
      const cashFlowResponse = await apiFetch(
        `/api/cash-flow-statement/pdf?period_start=${periodStart}&period_end=${periodEnd}`,
      );

      if (!cashFlowResponse.ok) {
        const message = await getErrorMessage(
          cashFlowResponse,
          '資金収支計算書のPDF生成に失敗しました',
        );
        throw new Error(message);
      }

      const cashFlowBlob = await cashFlowResponse.blob();
      const cashFlowUrl = URL.createObjectURL(cashFlowBlob);
      const cashFlowLink = document.createElement('a');
      cashFlowLink.href = cashFlowUrl;
      cashFlowLink.download = `資金収支計算書_${selectedFiscalYear}年度.pdf`;
      document.body.appendChild(cashFlowLink);
      cashFlowLink.click();
      document.body.removeChild(cashFlowLink);
      URL.revokeObjectURL(cashFlowUrl);

      // 事業活動収支計算書のPDFをダウンロード
      const businessActivityResponse = await apiFetch(
        `/api/business-activity-statement/pdf?period_start=${periodStart}&period_end=${periodEnd}`,
      );

      if (!businessActivityResponse.ok) {
        const message = await getErrorMessage(
          businessActivityResponse,
          '事業活動収支計算書のPDF生成に失敗しました',
        );
        throw new Error(message);
      }

      const businessActivityBlob = await businessActivityResponse.blob();
      const businessActivityUrl = URL.createObjectURL(businessActivityBlob);
      const businessActivityLink = document.createElement('a');
      businessActivityLink.href = businessActivityUrl;
      businessActivityLink.download = `事業活動収支計算書_${selectedFiscalYear}年度.pdf`;
      document.body.appendChild(businessActivityLink);
      businessActivityLink.click();
      document.body.removeChild(businessActivityLink);
      URL.revokeObjectURL(businessActivityUrl);
    } catch (err: any) {
      console.error('PDF生成エラー:', err);
      setError(err?.message || 'PDFの生成に失敗しました');
    } finally {
      setPdfGenerating(false);
    }
  };

  const totalCashFlowIncome = useMemo(() => {
    return monthlyData.reduce((sum, data) => sum + (data.cash_flow?.total_income || 0), 0);
  }, [monthlyData]);

  const totalCashFlowExpense = useMemo(() => {
    return monthlyData.reduce((sum, data) => sum + (data.cash_flow?.total_expense || 0), 0);
  }, [monthlyData]);

  const totalCashFlowNet = useMemo(() => {
    return monthlyData.reduce((sum, data) => sum + (data.cash_flow?.net_cash_flow || 0), 0);
  }, [monthlyData]);

  const totalBusinessActivityNet = useMemo(() => {
    return monthlyData.reduce((sum, data) => sum + (data.business_activity?.net_change || 0), 0);
  }, [monthlyData]);

  const fiscalYearOptions = useMemo(() => {
    const options = [];
    const currentYear = new Date().getFullYear();
    for (let year = currentYear - 5; year <= currentYear + 1; year++) {
      options.push(year);
    }
    return options;
  }, []);

  return (
    <>
      <div
        style={{
          backgroundColor: '#ffffff',
          border: '2px inset #d0d0d0',
          padding: '20px',
          maxWidth: '1000px',
          margin: '0 auto',
        }}
      >
        {/* 期間選択セクション */}
        <div
          style={{
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: '#f8f8f8',
            border: '1px solid #d0d0d0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>
              年度選択:
              <select
                value={selectedFiscalYear}
                onChange={handleYearChange}
                style={{
                  marginLeft: '8px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontFamily: "'MS UI Gothic', sans-serif",
                  border: '2px inset #d0d0d0',
                  backgroundColor: '#ffffff',
                }}
              >
                {fiscalYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}年度（{year}年4月〜{year + 1}年3月）
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={handleRefresh}
              disabled={loading}
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                fontFamily: "'MS UI Gothic', sans-serif",
                backgroundColor: '#e0e0e0',
                border: '2px outset #e0e0e0',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.currentTarget.style.border = '2px inset #e0e0e0';
                }
              }}
              onMouseUp={(e) => {
                if (!loading) {
                  e.currentTarget.style.border = '2px outset #e0e0e0';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.border = '2px outset #e0e0e0';
                }
              }}
            >
              {loading ? '更新中...' : '更新'}
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={pdfGenerating || loading}
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                fontFamily: "'MS UI Gothic', sans-serif",
                backgroundColor: '#e0e0e0',
                border: '2px outset #e0e0e0',
                cursor: pdfGenerating || loading ? 'not-allowed' : 'pointer',
                opacity: pdfGenerating || loading ? 0.6 : 1,
              }}
              onMouseDown={(e) => {
                if (!pdfGenerating && !loading) {
                  e.currentTarget.style.border = '2px inset #e0e0e0';
                }
              }}
              onMouseUp={(e) => {
                if (!pdfGenerating && !loading) {
                  e.currentTarget.style.border = '2px outset #e0e0e0';
                }
              }}
              onMouseLeave={(e) => {
                if (!pdfGenerating && !loading) {
                  e.currentTarget.style.border = '2px outset #e0e0e0';
                }
              }}
            >
              {pdfGenerating ? 'PDF生成中...' : 'PDF出力'}
            </button>
          </div>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '8px 12px',
              backgroundColor: '#ffe0e0',
              border: '1px solid #ff8080',
              color: '#cc0000',
              fontSize: '11px',
            }}
          >
            {error}
          </div>
        )}

        {/* 月次データテーブル */}
        {loading && monthlyData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
            データを読み込み中...
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11px',
                fontFamily: "'MS UI Gothic', sans-serif",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#0066cc', color: '#ffffff' }}>
                  <th
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #004499',
                      textAlign: 'left',
                      fontWeight: 'bold',
                    }}
                  >
                    年月
                  </th>
                  <th
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #004499',
                      textAlign: 'right',
                      fontWeight: 'bold',
                    }}
                  >
                    資金収支<br />収入
                  </th>
                  <th
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #004499',
                      textAlign: 'right',
                      fontWeight: 'bold',
                    }}
                  >
                    資金収支<br />支出
                  </th>
                  <th
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #004499',
                      textAlign: 'right',
                      fontWeight: 'bold',
                    }}
                  >
                    資金収支<br />差額
                  </th>
                  <th
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #004499',
                      textAlign: 'right',
                      fontWeight: 'bold',
                    }}
                  >
                    事業活動<br />収支差額
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((data, index) => (
                  <tr
                    key={`${data.year}-${data.month}`}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f8f8',
                    }}
                  >
                    <td
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #d0d0d0',
                        fontWeight: 'bold',
                      }}
                    >
                      {data.year}年{data.month}月
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #d0d0d0',
                        textAlign: 'right',
                      }}
                    >
                      {data.cash_flow
                        ? formatCurrency(data.cash_flow.total_income)
                        : '-'}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #d0d0d0',
                        textAlign: 'right',
                      }}
                    >
                      {data.cash_flow
                        ? formatCurrency(data.cash_flow.total_expense)
                        : '-'}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #d0d0d0',
                        textAlign: 'right',
                        fontWeight: 'bold',
                        color:
                          data.cash_flow && data.cash_flow.net_cash_flow >= 0
                            ? '#006600'
                            : '#cc0000',
                      }}
                    >
                      {data.cash_flow
                        ? formatCurrency(data.cash_flow.net_cash_flow)
                        : '-'}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #d0d0d0',
                        textAlign: 'right',
                        fontWeight: 'bold',
                        color:
                          data.business_activity && data.business_activity.net_change >= 0
                            ? '#006600'
                            : '#cc0000',
                      }}
                    >
                      {data.business_activity
                        ? formatCurrency(data.business_activity.net_change)
                        : '-'}
                    </td>
                  </tr>
                ))}
                {/* 合計行 */}
                <tr style={{ backgroundColor: '#e8e8e8', fontWeight: 'bold' }}>
                  <td
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #d0d0d0',
                    }}
                  >
                    合計
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #d0d0d0',
                      textAlign: 'right',
                    }}
                  >
                    {formatCurrency(totalCashFlowIncome)}
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #d0d0d0',
                      textAlign: 'right',
                    }}
                  >
                    {formatCurrency(totalCashFlowExpense)}
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #d0d0d0',
                      textAlign: 'right',
                      color: totalCashFlowNet >= 0 ? '#006600' : '#cc0000',
                    }}
                  >
                    {formatCurrency(totalCashFlowNet)}
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #d0d0d0',
                      textAlign: 'right',
                      color: totalBusinessActivityNet >= 0 ? '#006600' : '#cc0000',
                    }}
                  >
                    {formatCurrency(totalBusinessActivityNet)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};














export default ClassicMonthlySummary;
